import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, input, signal } from "@angular/core";
import { LvfDeliveryMode, LvfTicketArticleNature, type Ticket, type TicketDeliveryProduct } from "@kiraah-shared/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { formatTicketDeliveryAddressLines } from "../../../helpers/mobile-ticket-address-format";
import { mobileTicketIsProductLike, mobileTicketIsVehicleLike } from "../../../helpers/mobile-ticket-demand-display";
import { downloadMobileTicketDemandSummaryPdf } from "../../../helpers/mobile-ticket-demand-summary-pdf";
import { MobileAuthStore } from "../../../../state/mobile-auth.store";
import type { MobileTicketWizardComponent } from "../../mobile-ticket-wizard-page.component";

/** Vue de l’étape « récapitulatif » (parité web : `ngx-ticket-summary`). */
@Component({
  selector: "mobile-ticket-summary-step",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./mobile-ticket-summary-step.component.html",
  styleUrl: "./mobile-ticket-summary-step.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileTicketSummaryStepComponent {
  private readonly translate = inject(TranslateService);
  private readonly authStore = inject(MobileAuthStore);

  readonly wizard = input.required<MobileTicketWizardComponent>();
  readonly ticket = input.required<Ticket>();
  readonly saveHadError = input(false);

  protected readonly pdfDownloading = signal(false);

  protected summaryArticleNature(): LvfTicketArticleNature | undefined {
    return this.ticket().TicketType?.LvfTicketArticleNature ?? undefined;
  }

  /** Lignes type produit/service : colonnes Quantité / Unité. */
  protected summaryIsArticleTable(): boolean {
    const articleNature = this.summaryArticleNature();
    return articleNature === LvfTicketArticleNature.PERISHABLE_PRODUCT || articleNature === LvfTicketArticleNature.SERVICE;
  }

  /** Ligne équipement : colonnes Identification / immat · Désignation (sans quantité/unité). */
  protected summaryIsEquipmentTable(): boolean {
    return this.summaryArticleNature() === LvfTicketArticleNature.VEHICLE;
  }

  /** Ligne type d’équipement : libellé type · Places · Énergie. */
  protected summaryIsEquipmentTypeTable(): boolean {
    return this.summaryArticleNature() === LvfTicketArticleNature.VEHICLE_TYPE;
  }

  protected summaryArticlePrimaryColumnKey(): string {
    return this.summaryArticleNature() === LvfTicketArticleNature.SERVICE
      ? "mobile.eachat.wizard.summaryColService"
      : "mobile.eachat.wizard.summaryColProduct";
  }

  protected summaryLinesSectionTitleKey(): string {
    switch (this.summaryArticleNature()) {
      case LvfTicketArticleNature.VEHICLE:
        return "mobile.eachat.wizard.summaryPdfSectionVehicles";
      case LvfTicketArticleNature.VEHICLE_TYPE:
        return "mobile.eachat.wizard.summaryPdfSectionVehicleTypes";
      case LvfTicketArticleNature.SERVICE:
        return "mobile.eachat.wizard.summaryPdfSectionServices";
      default:
        return "mobile.eachat.wizard.summaryPdfSectionProducts";
    }
  }

  protected summaryEquipmentPrimaryLine(line: TicketDeliveryProduct): string {
    const identificationInfo = line.EquipmentVehicle?.IdentificationInfo?.trim();
    const registrationNumber = line.EquipmentVehicle?.RegistrationNumber?.trim() ?? "";
    if (identificationInfo) {
      return identificationInfo;
    }
    if (registrationNumber) {
      return registrationNumber;
    }
    return this.translate.instant("app.shared.notprovided");
  }

  /** Immat à afficher en second niveau lorsqu’elle n’est pas déjà utilisée comme libellé principal. */
  protected summaryEquipmentRegistrationLine(line: TicketDeliveryProduct): string {
    const identificationInfo = line.EquipmentVehicle?.IdentificationInfo?.trim();
    const registrationNumber = line.EquipmentVehicle?.RegistrationNumber?.trim() ?? "";
    if (registrationNumber && registrationNumber !== identificationInfo) {
      return registrationNumber;
    }
    return "";
  }

  protected summaryEquipmentDesignation(line: TicketDeliveryProduct): string {
    const designation = line.EquipmentVehicle?.Designation?.trim();
    return designation ? designation : this.translate.instant("app.shared.notprovided");
  }

  protected summaryEquipmentTypeLabel(line: TicketDeliveryProduct): string {
    return (
      line.EquipmentType?.Label?.trim() ||
      line.VehicleType?.Label?.trim() ||
      this.translate.instant("app.shared.notprovided")
    );
  }

  protected summaryEquipmentTypeSeats(line: TicketDeliveryProduct): string {
    const seatCount = line.EquipmentType?.VehicleTypeExtension?.NbOfSeats ?? line.VehicleType?.VehicleTypeExtension?.NbOfSeats;
    return seatCount != null && `${seatCount}` !== "" ? `${seatCount}` : this.translate.instant("app.shared.notprovided");
  }

  protected showLinesSection(): boolean {
    const ticket = this.ticket();
    return mobileTicketIsProductLike(ticket) || mobileTicketIsVehicleLike(ticket);
  }

  /** Track sans référencer `@let w` (interdit dans l’expression `track` du `@for`). */
  protected deliveryLineTrack(index: number, line: TicketDeliveryProduct): string {
    return this.wizard().selectionLineTrack(index, line);
  }

  protected deliveryModeLabel(): string {
    const ticket = this.ticket();
    const deliveryMode = ticket.Delivery?.LvfDeliveryMode ?? LvfDeliveryMode.DIRECT_TO_REQUESTER;
    switch (deliveryMode) {
      case LvfDeliveryMode.DIRECT_TO_REQUESTER:
        return this.translate.instant("mobile.eachat.wizard.delivery.directRequester");
      case LvfDeliveryMode.DIRECT_TO_RECIPIENT:
        return this.translate.instant("mobile.eachat.wizard.delivery.directRecipient");
      case LvfDeliveryMode.SPECIFIED_AGENCY:
        return this.translate.instant("mobile.eachat.wizard.delivery.specifiedAgency");
      case LvfDeliveryMode.MANUAL_INPUT_ADDRESS:
        return this.translate.instant("mobile.eachat.wizard.delivery.manualAddress");
      default:
        return this.translate.instant("app.shared.notprovided");
    }
  }

  protected deliveryAddressLine(): string {
    const wizard = this.wizard();
    const ticket = this.ticket();
    const preview = wizard.previewDeliveryResolvedAddressLines();
    if (preview.length) {
      return preview.join(", ");
    }
    const address = wizard.deliveryManualAddress() ?? ticket.Delivery?.EntityAddress;
    return formatTicketDeliveryAddressLines(address).join(", ");
  }

  protected async onDownloadPdf(): Promise<void> {
    if (this.pdfDownloading()) {
      return;
    }
    const wizard = this.wizard();
    const ticket = this.ticket();
    const notProvided = this.translate.instant("app.shared.notprovided");
    const lang = this.translate.currentLang || "fr";
    const creationDate =
      (ticket.CreationDateString ?? "").trim() ||
      new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB");
    const authorIdentity = this.authStore.authResult()?.ProfilData?.Identity;
    const authorName =
      ticket.Author?.FullName?.trim() ||
      authorIdentity?.FullName?.trim() ||
      [authorIdentity?.FirstName, authorIdentity?.BirthName].filter(Boolean).join(" ").trim() ||
      notProvided;
    this.pdfDownloading.set(true);
    try {
      const nature = ticket.TicketType?.LvfTicketArticleNature;
      const detailTable =
        nature === LvfTicketArticleNature.VEHICLE
          ? ({
              variant: "equipment",
              rows: wizard.deliveryProducts().map((line: TicketDeliveryProduct) => {
                const primaryRaw = (
                  line.EquipmentVehicle?.IdentificationInfo?.trim() ||
                  line.EquipmentVehicle?.RegistrationNumber?.trim() ||
                  ""
                ).trim();
                const regOnly = line.EquipmentVehicle?.RegistrationNumber?.trim() || "";
                return {
                  identificationPrimary:
                    primaryRaw || notProvided,
                  registrationLine:
                    regOnly && regOnly !== line.EquipmentVehicle?.IdentificationInfo?.trim()
                      ? regOnly
                      : "",
                  designation: line.EquipmentVehicle?.Designation?.trim() || notProvided,
                };
              }),
            } as const)
          : nature === LvfTicketArticleNature.VEHICLE_TYPE
            ? ({
                variant: "vehicle_type",
                rows: wizard.deliveryProducts().map((line: TicketDeliveryProduct) => ({
                  label: line.EquipmentType?.Label?.trim() || line.VehicleType?.Label?.trim() || notProvided,
                  seats:
                    (line.EquipmentType?.VehicleTypeExtension?.NbOfSeats ?? line.VehicleType?.VehicleTypeExtension?.NbOfSeats) != null
                      ? `${line.EquipmentType?.VehicleTypeExtension?.NbOfSeats ?? line.VehicleType?.VehicleTypeExtension?.NbOfSeats}`
                      : notProvided,
                  energy: wizard.equipmentLineEnergyLabel(line),
                })),
              } as const)
            : ({
                variant: "article",
                primaryHeaderKey:
                  nature === LvfTicketArticleNature.SERVICE
                    ? "mobile.eachat.wizard.summaryColService"
                    : "mobile.eachat.wizard.summaryColProduct",
                rows: wizard.deliveryProducts().map((line: TicketDeliveryProduct) => ({
                  title: wizard.mobileDeliveryProductTitle(line),
                  subtitle: wizard.mobileDeliveryProductSubtitle(line),
                  quantity: String(line.Quantity ?? ""),
                  unit: line.Unit?.Name?.trim() || "—",
                })),
              } as const);

      await downloadMobileTicketDemandSummaryPdf(
        {
          label: wizard.generalForm.controls["label"].value.trim() || notProvided,
          description: wizard.generalForm.controls["description"].value.trim() || notProvided,
          category: ticket.TaskCategoryLabel?.trim() || notProvided,
          requester: wizard.mobileTicketRequesterLabel(ticket).trim() || notProvided,
          recipient: wizard.mobileTicketRecipientLabel(ticket).trim() || notProvided,
          creationDate,
          authorName,
          deliveryMode: this.deliveryModeLabel(),
          deliveryAddress: this.deliveryAddressLine().trim(),
          detailTable,
          ticket,
        },
        this.translate,
      );
    } finally {
      this.pdfDownloading.set(false);
    }
  }
}
